import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, useLocation, useParams } from '@reach/router';
import ReactMarkdown from 'react-markdown';
import { Flex, FlexBehavior, FlexJustify, FlexWrap, Spinner } from 'react-magma-dom';
import { LessonsHeaderContainer } from '../LessonsHeader';
import { LessonSideNav } from './LessonSideNav';
import { Audience } from './Audience';
import { LessonText } from './LessonText';
import { LessonHeading } from './LessonHeading';
import { LessonWrapper } from './LessonWrapper';
import { LessonTable } from './LessonTable';
import { DownloadDocument } from './DownloadDocument';
import { ContactUsError } from '../ContactUsError';
import {
  LessonsContainer,
  LessonsSubHeader,
  MarginWrapper,
  StyledCenteredSpinnerWrapper,
} from '../Styled/StyledWrappers';
import { StyledColoredLine } from '../Styled';
import { lessonsApi } from '../../API/lessons';
import { lessonsNavLinks } from '../../pages/Lessons/config';
import {
  LessonContent,
  LessonNavItem,
  LessonPage,
  LessonPageHeading,
  LessonPageTable,
  LessonPageText,
  LessonPageWrapper,
  LessonPdf,
} from '../../types/lessons';
import { useQuery } from '../../hooks/useQuery';
import { computeNavItems, getStageColors } from '../../utils';
import { CONTENT_TYPES } from '../../constants/lessons';
import { LessonContext } from '../../contexts/lessonContext';
import { COLORS } from '../../constants';
import { ComingSoon } from '../ComingSoon';
import { getLessonAvailability } from '../../utils/envs';
import AuthContext from '../../contexts/authContext';

export const Lesson: React.FC<RouteComponentProps> = () => {
  const { state, setStageAndTextType } = useContext(LessonContext);
  const { stage } = state;

  const [lesson, setLesson] = useState<LessonContent | null>(null);
  const [pdf, setPdf] = useState<LessonPdf | null>(null);
  const [backLinkNames, setBackLinkName] = useState<string[]>([]);
  const [navItems, setNavItems] = useState<LessonNavItem[]>([]);
  const [activePage, setActivePage] = useState<LessonPage | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const { setDefaultStage } = useContext(AuthContext);

  const { pathname } = useLocation();
  const params = useParams();
  const pageId = useQuery('page');

  const { lessonId } = params;
  const { main: mainColor, gradient: gradientColor } = getStageColors(stage.toLowerCase());

  useEffect(() => {
    if (pageId) {
      lesson?.content.forEach(({ content: pages }) => {
        const currentPage = pages.find(({ id }) => id === +pageId);
        if (currentPage) {
          setActivePage(currentPage);
          return;
        }
      });
    }
  }, [pageId, lesson]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: {
            data,
            meta: { collectionName, textTypeName, exemplarName, stageName },
          },
        } = await lessonsApi.getLesson(lessonId);

        setIsComingSoon(!getLessonAvailability(stageName));
        setLesson(data.content);
        setPdf(data.pdf);
        !pageId && setActivePage(data.content.content[0].content[0]);
        setBackLinkName(['Find a lesson plans', collectionName, textTypeName, exemplarName]);
        setStageAndTextType(stageName, textTypeName);
        setNavItems(computeNavItems(data.content.content));
        setDefaultStage(stageName.toLocaleLowerCase());
      } catch (error) {
        setError(!!error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (isComingSoon) {
    return <ComingSoon stageNotReady />;
  }

  if (isLoading) {
    return (
      <StyledCenteredSpinnerWrapper behavior={FlexBehavior.container} justify={FlexJustify.center}>
        <Spinner color={COLORS.seaBlue} size={50} />
      </StyledCenteredSpinnerWrapper>
    );
  } else if (error) {
    return <ContactUsError />;
  }

  const renderActivePage = () => {
    if (!activePage) {
      return null;
    }

    const { title, audience, content } = activePage;

    return (
      <>
        <LessonsSubHeader tabIndex={-1} data-testid="lesson-header" withBorderBottom>
          <ReactMarkdown skipHtml={false}>{title}</ReactMarkdown>
          {pdf && <DownloadDocument pdf={pdf} color={mainColor} />}
        </LessonsSubHeader>
        {audience && <Audience audience={audience} />}
        <MarginWrapper margin="20px 0 40px 0">
          {content.map(
            (contentItem, index) =>
              (contentItem.type === CONTENT_TYPES.heading && (
                <LessonHeading
                  {...(contentItem as LessonPageHeading)}
                  isTooltipShown
                  isStandalone
                  linkColor={mainColor}
                  key={index}
                />
              )) ||
              (contentItem.type === CONTENT_TYPES.text && (
                <LessonText
                  {...(contentItem as LessonPageText)}
                  isStandalone
                  isTooltipShown
                  linkColor={mainColor}
                  key={index}
                />
              )) ||
              (contentItem.type === CONTENT_TYPES.wrapper && (
                <LessonWrapper {...(contentItem as LessonPageWrapper)} linkColor={mainColor} key={index} />
              )) ||
              (contentItem.type === CONTENT_TYPES.table && (
                <LessonTable {...(contentItem as LessonPageTable)} linkColor={mainColor} key={index} />
              ))
          )}
        </MarginWrapper>
      </>
    );
  };

  return (
    <>
      <StyledColoredLine color={gradientColor} />
      <Flex behavior={FlexBehavior.container} wrap={FlexWrap.nowrap}>
        <LessonsContainer width={1000}>
          <LessonsHeaderContainer
            navLinks={lessonsNavLinks.filter(link => pathname.match(link.regExp))}
            params={{
              ...params,
              names: backLinkNames,
            }}
          />
          {renderActivePage()}
        </LessonsContainer>
        <LessonSideNav navItems={navItems} activeItemId={activePage?.id} />
      </Flex>
    </>
  );
};
